import React, { useEffect } from 'react';
import { Helmet } from 'react-helmet-async';
import { useNavigate, Link } from 'react-router-dom';
import {
  ArrowRightOutlined,
  CheckCircleOutlined,
  SearchOutlined,
  VideoCameraOutlined,
  PictureOutlined,
  FilePdfOutlined,
  AppstoreOutlined,
  DashboardOutlined,
  CodeOutlined,
} from '@ant-design/icons';
import { useAuth } from '../../contexts/AuthContext';
import { useApiKeyManager } from '../ApiKey/ApiKeyManagerProvider';
import { saveIntendedUrl } from '../../utils/persistentNavigation';
import { trackEvent } from '../../utils/analytics';
import { SEO_DEFAULTS } from '../../utils/seoConfig';
import { JsonLd } from '../../utils/structuredData';
import BenchmarkBadge from './BenchmarkBadge';
import MobileStickyCta from './MobileStickyCta';
import useReveal from '../AIEmployees/useReveal';
import '../AIEmployees/AIEmployeesLandingPage.css';
import './IntegrationLandingPage.css';

/**
 * IntegrationLandingPage — ONE shared marketing template for every integration
 * page (/hermes, /cursor, /claude-code, /codex). Driven by a config object from
 * integrationConfigs.js. Renders on the .aie-landing design system inside the
 * MarketingRoute shell (dark, top header, no sidebar when logged out).
 *
 * Two install stories:
 *  - installKind: 'plugin' → a single install command (Open Plugins tools)
 *  - installKind: 'sdk'    → the CellCog Python SDK snippet (Hermes etc.)
 *
 * SEO/AEO: each page ships a PER-PLATFORM SoftwareApplication schema (name,
 * keywords, description from its config — previously all four shared the
 * generic agent-to-agent schema, so Google saw duplicate applications) plus
 * FAQPage structured data from config.faqs, rendered on-page answer-first.
 *
 * Conversion flow (SDK pages, no ctaHref): logged-in → ApiKeyManager modal;
 * logged-out → intent stashed via persistentNavigation (the real post-auth
 * rail; the old `?return=` query param was dead code — /signup never read it),
 * auto-opening the modal on post-signup return.
 */
const SKILLS = [
  { Icon: SearchOutlined, label: 'Deep research' },
  { Icon: VideoCameraOutlined, label: 'Video' },
  { Icon: PictureOutlined, label: 'Images' },
  { Icon: FilePdfOutlined, label: 'PDFs & docs' },
  { Icon: AppstoreOutlined, label: 'Apps' },
  { Icon: DashboardOutlined, label: 'Dashboards' },
  { Icon: CodeOutlined, label: 'Code' },
];

const SdkSnippet = ({ providerValue }) => (
  <pre
    className="int-install__code"
    dangerouslySetInnerHTML={{
      __html:
        '<span class="tok-com"># Reads CELLCOG_API_KEY from the environment</span>\n'
        + '<span class="tok-kw">from</span> cellcog <span class="tok-kw">import</span> CellCogClient\n\n'
        + `client = CellCogClient(agent_provider=<span class="tok-str">"${providerValue}"</span>)\n\n`
        + 'result = client.create_chat(\n'
        + '    prompt=<span class="tok-str">"Research the EV battery market and make a 10-page PDF"</span>,\n'
        + '    task_label=<span class="tok-str">"ev-battery-research"</span>,\n'
        + '    chat_mode=<span class="tok-str">"agent"</span>,\n'
        + ')\n'
        + '<span class="tok-kw">print</span>(result[<span class="tok-str">"chat_id"</span>])',
    }}
  />
);

const IntegrationLandingPage = ({ config }) => {
  const navigate = useNavigate();
  const { currentUser } = useAuth();
  const { open: openApiKeyManager } = useApiKeyManager();
  const [propsRef, propsVisible] = useReveal();
  const [installRef, installVisible] = useReveal();
  const [faqRef, faqVisible] = useReveal();

  const canonicalUrl = `${SEO_DEFAULTS.url}/${config.slug}`;
  const ogImage = `${SEO_DEFAULTS.url}/og-image.png`;

  // Per-platform SoftwareApplication schema — built from this page's config so
  // each integration page describes ITSELF to search/answer engines.
  const integrationSchema = {
    '@context': 'https://schema.org',
    '@type': 'SoftwareApplication',
    name: `CellCog for ${config.name}`,
    applicationCategory: 'DeveloperApplication',
    applicationSubCategory: 'AI Agent Integration',
    operatingSystem: 'Web',
    description: config.seo.description,
    ...(config.keywords ? { keywords: config.keywords } : {}),
    url: canonicalUrl,
    offers: {
      '@type': 'Offer',
      price: '8',
      priceCurrency: 'USD',
      description: 'Try it free, no credit card needed. Usage-based billing; plans from $8/month.',
    },
    publisher: { '@type': 'Organization', name: 'CellCog', url: SEO_DEFAULTS.url },
  };
  const faqSchema = config.faqs && config.faqs.length
    ? {
      '@context': 'https://schema.org',
      '@type': 'FAQPage',
      mainEntity: config.faqs.map((f) => ({
        '@type': 'Question',
        name: f.q,
        acceptedAnswer: { '@type': 'Answer', text: f.a },
      })),
    }
    : null;

  // Primary CTA: external link (plugin/directory) opens out; SDK pages get the
  // API key flow (modal when logged in, signup-with-intent when logged out).
  const handlePrimary = (location) => {
    trackEvent('cta_click', {
      cta_name: config.ctaHref ? 'get_plugin' : 'get_api_key',
      location: typeof location === 'string' ? location : 'page',
      page: config.slug,
    });
    if (config.ctaHref) {
      window.open(config.ctaHref, '_blank', 'noopener,noreferrer');
      return;
    }
    if (!currentUser) {
      saveIntendedUrl(`/${config.slug}?intent=get_api_key`);
      navigate('/signup');
      return;
    }
    openApiKeyManager();
  };

  // Post-signup auto-open (SDK pages): consume ?intent=get_api_key on return.
  useEffect(() => {
    if (!currentUser) return;
    const params = new URLSearchParams(window.location.search);
    if (params.get('intent') === 'get_api_key') {
      params.delete('intent');
      const url = new URL(window.location);
      url.search = params.toString();
      window.history.replaceState({}, document.title, url.toString());
      openApiKeyManager();
    }
  }, [currentUser, openApiKeyManager]);

  return (
    <>
      <Helmet>
        <title>{config.seo.title}</title>
        <meta name="description" content={config.seo.description} />
        <link rel="canonical" href={canonicalUrl} />
        <link rel="alternate" type="text/markdown" href={`${canonicalUrl}/index.md`} />
        <meta property="og:type" content="website" />
        <meta property="og:title" content={config.seo.title} />
        <meta property="og:description" content={config.seo.description} />
        <meta property="og:url" content={canonicalUrl} />
        <meta property="og:image" content={ogImage} />
        <meta property="og:site_name" content={SEO_DEFAULTS.siteName} />
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:title" content={config.seo.title} />
        <meta name="twitter:description" content={config.seo.description} />
        <link rel="preconnect" href="https://fonts.googleapis.com" />
        <link rel="preconnect" href="https://fonts.gstatic.com" crossOrigin="anonymous" />
        <link
          href="https://fonts.googleapis.com/css2?family=Inter:wght@400;500;600;700&family=Inter+Tight:wght@500;600;700;800&family=JetBrains+Mono:wght@400;500;600&display=swap"
          rel="stylesheet"
        />
      </Helmet>
      <JsonLd data={integrationSchema} />
      {faqSchema && <JsonLd data={faqSchema} />}

      <div className="aie-landing dark int-landing">
        {/* Hero */}
        <section className="int-hero">
          <div className="int-hero__glow" aria-hidden="true" />
          <div className="int-hero__inner">
            {/* TWO-FACE RULE (adaptive shell, Aug 23): config.logo is the
                dark-face artwork; configs with a logoLight twin render both
                imgs and the adaptive CSS shows one per face. The mono CC
                mark (white ink) gets its teal twin on the light face. */}
            <div className="int-hero__lockup">
              <img
                className={`int-hero__logo${config.logoLight ? ' mkt-face-dark' : ''}`}
                src={config.logo}
                alt={`${config.name} logo`}
              />
              {config.logoLight && (
                <img
                  className="int-hero__logo mkt-face-light"
                  src={config.logoLight}
                  alt=""
                />
              )}
              <span className="int-hero__x">×</span>
              <img
                className="int-hero__cc-mark mkt-face-dark"
                src="/assets/cellcog/cellcog-mark-mono.png"
                alt="CellCog"
              />
              <img
                className="int-hero__cc-mark mkt-face-light"
                src="/assets/linear/cellcog-mark-dark.png"
                alt=""
              />
            </div>
            <h1 className="int-hero__title aie-display">
              {config.headline}
              <br />
              <span className="int-hero__title-accent">{config.headlineAccent}</span>
            </h1>
            <p className="int-hero__sub">{config.tagline}</p>
            <BenchmarkBadge page={config.slug} />
            <div className="int-hero__ctas">
              <button type="button" data-msc-suppress className="aie-btn aie-btn-primary" onClick={() => handlePrimary('hero')}>
                {config.ctaLabel} <ArrowRightOutlined />
              </button>
            </div>
            <p className="int-hero__note">
              Try it free, no credit card needed. Every account starts with 200 credits.
            </p>
          </div>
        </section>

        {/* What you get */}
        <section className="int-props aie-section">
          <div className="aie-section-head">
            <h2 className="aie-section-title aie-display">What your agent gets</h2>
          </div>
          <div className={`int-props__grid aie-reveal ${propsVisible ? 'is-visible' : ''}`} ref={propsRef}>
            {config.valueProps.map((p) => (
              <div className="int-props__card" key={p.title}>
                <CheckCircleOutlined className="int-props__icon" />
                <h3 className="int-props__title">{p.title}</h3>
                <p className="int-props__body">{p.body}</p>
              </div>
            ))}
          </div>
        </section>

        {/* Install */}
        <section className="int-install aie-section" ref={installRef}>
          <div className={`int-install__inner aie-reveal ${installVisible ? 'is-visible' : ''}`}>
            <div className="aie-section-head">
              <h2 className="aie-section-title aie-display">
                {config.installKind === 'plugin' ? 'Install the plugin' : 'Connect the SDK'}
              </h2>
              <p className="aie-section-sub">
                {config.installKind === 'plugin'
                  ? 'One command, then CellCog is available inside your agent.'
                  : 'pip install cellcog, set your API key, and delegate in one call.'}
              </p>
            </div>
            <div className="int-install__panel">
              <div className="int-install__head">
                <span className="int-install__dot" />
                <span className="int-install__dot" />
                <span className="int-install__dot" />
                <span className="int-install__name">
                  {config.installKind === 'plugin' ? 'terminal' : 'your_agent.py'}
                </span>
              </div>
              {config.installKind === 'plugin' ? (
                <pre className="int-install__code">
                  <span className="tok-com"># Install the CellCog plugin (Open Plugins standard)</span>
                  {'\n'}
                  {config.installCommand}
                </pre>
              ) : (
                <SdkSnippet providerValue={config.providerValue} />
              )}
            </div>
            {/* The second way in (Nitish, Oct 11 2026): MCP, no API key; both ways explained on /agent-to-agent. */}
            {config.mcp && (
              <p className="int-install__mcp">
                {config.mcp.intro}{' '}
                {config.mcp.commands.map((cmd, i) => (
                  <React.Fragment key={cmd}>
                    {i > 0 && ' then '}
                    <code>{cmd}</code>
                  </React.Fragment>
                ))}
                {'. '}
                {config.mcp.outro}{' '}
                <Link
                  to="/agent-to-agent"
                  onClick={() => trackEvent('cta_click', { cta_name: 'mcp_two_ways', location: 'install', page: config.slug })}
                >
                  Both ways, explained <ArrowRightOutlined />
                </Link>
              </p>
            )}
          </div>
        </section>

        {/* Subagents (Nitish, Oct 12 2026): the other way round, a CellCog chat or AI employee hands work TO this tool. */}
        {config.subagents && (
          <section className="int-subagents aie-section">
            <div className="aie-section-head">
              <span className="aie-eyebrow">The other way round</span>
              <h2 className="aie-section-title aie-display">{config.subagents.title}</h2>
              <p className="aie-section-sub">{config.subagents.body}</p>
            </div>
            <div className="int-subagents__links">
              <a
                href="/blog/ai-employee-subagents/"
                onClick={() => trackEvent('cta_click', { cta_name: 'subagents_post', location: 'subagents', page: config.slug })}
              >
                How subagents work <ArrowRightOutlined />
              </a>
              <a
                href="/support/subagents-guide"
                onClick={() => trackEvent('cta_click', { cta_name: 'subagents_guide', location: 'subagents', page: config.slug })}
              >
                Set it up <ArrowRightOutlined />
              </a>
            </div>
          </section>
        )}

        {/* Skills strip */}
        <section className="int-skills aie-section">
          <div className="aie-section-head">
            <h2 className="aie-section-title aie-display">One key. Every modality.</h2>
          </div>
          <div className="int-skills__grid">
            {SKILLS.map(({ Icon, label }) => (
              <div className="int-skills__tile" key={label}>
                <span className="int-skills__icon"><Icon /></span>
                <span className="int-skills__label">{label}</span>
              </div>
            ))}
          </div>
          <button
            type="button"
            className="int-skills__a2a-link"
            onClick={() => navigate('/agent-to-agent')}
          >
            See the full agent-to-agent platform <ArrowRightOutlined />
          </button>
        </section>

        {/* FAQ (AEO surface — questions carry this platform's category keywords) */}
        {config.faqs && config.faqs.length > 0 && (
          <section className="int-faq aie-section" ref={faqRef}>
            <div className="aie-section-head">
              <h2 className="aie-section-title aie-display">Common questions</h2>
            </div>
            <div className={`int-faq__list aie-reveal ${faqVisible ? 'is-visible' : ''}`}>
              {config.faqs.map((f) => (
                <div className="int-faq__item" key={f.q}>
                  <h3 className="int-faq__q">{f.q}</h3>
                  <p className="int-faq__a">{f.a}</p>
                </div>
              ))}
            </div>
          </section>
        )}

        {/* Close */}
        <section className="int-close">
          <div className="int-close__glow" aria-hidden="true" />
          <div className="int-close__inner">
            <h2 className="int-close__title aie-display">
              Bring CellCog into {config.name}.
            </h2>
            <button type="button" data-msc-suppress className="aie-btn aie-btn-primary int-close__cta" onClick={() => handlePrimary('close')}>
              {config.ctaLabel} <ArrowRightOutlined />
            </button>
          </div>
        </section>
        <MobileStickyCta label={config.ctaLabel} onClick={() => handlePrimary('sticky_bar')} />
      </div>
    </>
  );
};

export default IntegrationLandingPage;
